import { ComponentProps, useMemo } from 'react';
import { styled } from 'styled-components';
import { useUpMedia } from '../../hooks';
import { MobileText, DesktopText } from '../texts';
import { Slider as DefSlider, SliderProps } from './Slider.tsx';

export type SliderWithValuesProps = {
  current: string;
  max: string;
  sliderProps?: SliderProps;
} & ComponentProps<typeof Wrapper>;

const SliderWithValues = ({
  current,
  max,
  sliderProps,
  ...props
}: SliderWithValuesProps) => {
  const desktop = useUpMedia('md');

  const { CurrentValue, MaxValue } = useMemo(() => {
    return {
      CurrentValue: desktop ? CurrentDesktopValue : CurrentMobileValue,
      MaxValue: desktop ? MaxDesktopValue : MaxMobileValue,
    };
  }, [desktop]);

  return (
    <Wrapper {...props}>
      <CurrentValue>{current}</CurrentValue>
      <MaxValue>{max}</MaxValue>
      <Slider {...sliderProps} />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 2px;

  ${({ theme }) => theme.getUpMedia('md')} {
    grid-template-columns: auto 1fr auto;
    grid-column-gap: ${({ theme }) => theme.responsive(22)};
    align-items: center;
  }
`;

const MobileValue = styled(MobileText)`
  font-size: 14px;
  line-height: ${21 / 14};
  font-weight: 600;
`;

const CurrentMobileValue = styled(MobileValue)`
  color: ${({ theme }) => theme.getColor('completelyWhite')};
`;

const MaxMobileValue = styled(MobileValue)`
  justify-self: end;
  color: ${({ theme }) => theme.getColor('accentSecondary')};
`;

const DesktopValue = styled(DesktopText)`
  font-size: ${({ theme }) => theme.responsive(24)};
  line-height: 1;
  font-weight: 600;
  letter-spacing: -0.48px;
`;

const CurrentDesktopValue = styled(DesktopValue)`
  color: ${({ theme }) => theme.getColor('completelyWhite')};
`;

const MaxDesktopValue = styled(DesktopValue)`
  justify-self: end;
  color: ${({ theme }) => theme.getColor('accentSecondary')};
  order: 1;
`;

const Slider = styled(DefSlider)`
  grid-column: 1 / -1;
  margin-top: -4px;
  ${({ theme }) => theme.getUpMedia('md')} {
    margin-top: unset;
    grid-column: unset;
  }
`;

export { SliderWithValues };
